(() => {
  // The compare page used to load this asset from the section as well as the
  // global layout. Keep initialization idempotent while older cached HTML
  // expires, so duplicate script tags cannot double-bind click handlers.
  if (window.__roadSpecCompareLoaded) return;
  window.__roadSpecCompareLoaded = true;

  const storageKey = 'roadspec_compare_products_v1';
  const maxItems = 4;
  const track = (eventName, payload = {}) => {
    try {
      window.Shopify?.analytics?.publish(eventName, payload);
    } catch (_) {}
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[character]));

  const readItems = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((item) => item && item.variantId && item.handle).slice(0, maxItems);
    } catch (_) {
      return [];
    }
  };

  const writeItems = (items) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(items.slice(0, maxItems)));
    } catch (_) {
      // Private browsing or storage restrictions should not break the storefront.
    }
  };

  const formatPrice = (item) => {
    if (item.priceDisplay) return item.priceDisplay;
    const cents = Number(item.priceCents);
    if (!Number.isFinite(cents)) return 'See listing';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  };

  const itemFromButton = (button) => ({
    productId: button.dataset.productId || '',
    handle: button.dataset.productHandle || '',
    url: button.dataset.productUrl || '#',
    title: button.dataset.title || 'RoadSpec part',
    imageUrl: button.dataset.imageUrl || '',
    variantId: button.dataset.variantId || '',
    priceCents: button.dataset.priceCents || '',
    priceDisplay: button.dataset.priceDisplay || '',
    condition: button.dataset.condition || '',
    application: button.dataset.application || '',
    reference: button.dataset.reference || '',
    sku: button.dataset.sku || '',
    configuration: button.dataset.configuration || '',
    position: button.dataset.position || '',
    available: button.dataset.available === 'true',
  });

  let items = readItems();
  const pageRoot = document.querySelector('[data-rsp-compare-root]');
  const bar = document.querySelector('[data-rsp-compare-bar]');

  const announce = (message) => {
    const target = document.querySelector('[data-rsp-compare-message], [data-rsp-compare-status]');
    if (!target) return;
    target.textContent = message;
    window.setTimeout(() => {
      if (target.textContent === message) target.textContent = '';
    }, 4200);
  };

  const syncButtons = () => {
    document.querySelectorAll('[data-rsp-compare-add]').forEach((button) => {
      const saved = items.some((item) => item.variantId === button.dataset.variantId);
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      button.setAttribute('aria-label', `${saved ? 'Remove' : 'Add'} ${button.dataset.title || 'part'} ${saved ? 'from' : 'to'} compare`);
      const label = button.querySelector('[data-rsp-compare-label]');
      if (label) label.textContent = saved ? 'Saved' : 'Compare';
      const icon = button.querySelector('[aria-hidden="true"]');
      if (icon) icon.textContent = saved ? '✓' : '＋';
    });
  };

  const renderBar = () => {
    if (!bar) return;
    bar.hidden = items.length === 0;
    const count = bar.querySelector('[data-rsp-compare-count]');
    if (count) count.textContent = String(items.length);
  };

  const renderEmptyState = (empty, filled) => {
    if (!empty || !filled) return;
    empty.hidden = items.length > 0;
    filled.hidden = items.length === 0;
    const pageCount = filled.querySelector('[data-rsp-compare-page-count]');
    if (pageCount) pageCount.textContent = String(items.length);
  };

  const comparisonRows = [
    ['application', 'Vehicle application'],
    ['reference', 'Reference / OEM number'],
    ['sku', 'RoadSpec SKU'],
    ['configuration', 'Configuration'],
    ['position', 'Position / side'],
    ['condition', 'Condition'],
    ['priceDisplay', 'Price'],
  ];

  const renderTable = () => {
    if (!pageRoot) return;
    const tableWrap = pageRoot.querySelector('[data-rsp-compare-table-wrap]');
    const empty = pageRoot.querySelector('[data-rsp-compare-empty]');
    const filled = pageRoot.querySelector('[data-rsp-compare-filled]');
    renderEmptyState(empty, filled);
    if (!tableWrap || items.length === 0) return;

    const highlight = pageRoot.querySelector('[data-rsp-compare-highlight]')?.checked !== false;
    const headerCells = items.map((item, index) => `
      <th scope="col" class="rsp-compare-table__product">
        <a href="${escapeHtml(item.url)}" class="rsp-compare-table__product-link">
          ${item.imageUrl ? `<img src="${escapeHtml(item.imageUrl)}" alt="" loading="lazy" width="160" height="160">` : '<span class="rsp-compare-table__image-placeholder" aria-hidden="true">Part</span>'}
          <span>${escapeHtml(item.title)}</span>
        </a>
        <strong class="rsp-compare-table__price">${escapeHtml(formatPrice(item))}</strong>
        ${item.available ? `<button type="button" class="button button--primary rsp-compare-table__cart" data-rsp-compare-add-cart data-index="${index}">Add to cart</button>` : '<span class="rsp-compare-table__sold-out">Sold out</span>'}
        <button type="button" class="rsp-compare-table__remove" data-rsp-compare-remove data-index="${index}">Remove</button>
      </th>`).join('');

    const rows = comparisonRows.map(([key, label]) => {
      const values = items.map((item) => item[key] || '—');
      const uniqueValues = new Set(values.filter((value) => value !== '—'));
      const differs = uniqueValues.size > 1;
      return `<tr data-rsp-compare-row="${key}" class="${highlight && differs ? 'is-different' : ''}">
        <th scope="row">${escapeHtml(label)}</th>
        ${values.map((value) => `<td data-rsp-compare-cell>${escapeHtml(value)}</td>`).join('')}
      </tr>`;
    }).join('');

    tableWrap.innerHTML = `<div class="rsp-compare-table-scroll"><table class="rsp-compare-table"><thead><tr><th scope="col">Compare</th>${headerCells}</tr></thead><tbody>${rows}</tbody></table></div>`;
  };

  const refresh = () => {
    writeItems(items);
    renderBar();
    syncButtons();
    renderTable();
  };

  document.addEventListener('click', (event) => {
    const compareButton = event.target.closest('[data-rsp-compare-add]');
    if (compareButton) {
      const key = compareButton.dataset.variantId;
      const existingIndex = items.findIndex((item) => item.variantId === key);
      if (existingIndex >= 0) {
        items.splice(existingIndex, 1);
        announce('Removed from compare.');
        track('roadspec_compare_removed', { count: items.length });
      } else if (items.length >= maxItems) {
        announce(`Compare up to ${maxItems} parts. Remove one before saving another.`);
      } else {
        items.push(itemFromButton(compareButton));
        announce('Saved for comparison.');
        track('roadspec_compare_added', { count: items.length });
      }
      refresh();
      return;
    }

    const removeButton = event.target.closest('[data-rsp-compare-remove]');
    if (removeButton) {
      items.splice(Number(removeButton.dataset.index), 1);
      refresh();
      return;
    }

    const clearButton = event.target.closest('[data-rsp-compare-clear], [data-rsp-compare-page-clear]');
    if (clearButton) {
      items = [];
      refresh();
      announce('Comparison list cleared.');
      return;
    }

    const cartButton = event.target.closest('[data-rsp-compare-add-cart]');
    if (cartButton) {
      const item = items[Number(cartButton.dataset.index)];
      if (!item?.variantId) return;
      cartButton.disabled = true;
      cartButton.textContent = 'Adding…';
      const addUrl = `${window.routes?.cart_add_url || '/cart/add'}.js`;
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(item.variantId), quantity: 1 }] }),
      }).then((response) => {
        if (!response.ok) throw new Error('Cart add failed');
        window.location.assign(window.routes?.cart_url || '/cart');
      }).catch(() => {
        cartButton.disabled = false;
        cartButton.textContent = 'Add to cart';
        announce('We could not add this item. Open the product page and try again.');
      });
    }
  });

  document.querySelector('[data-rsp-compare-highlight]')?.addEventListener('change', renderTable);
  window.addEventListener('storage', (event) => {
    if (event.key === storageKey) {
      items = readItems();
      refresh();
    }
  });

  refresh();
})();
